import { EM_DASH } from "constants/index";

import { useContext, VFC } from "react";

import { CalendarToday } from "@material-ui/icons";
import { Button, Paper, portexColor, TextInput } from "@portex-pro/ui-components";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useGetPartnersRequestQuery } from "api/rest/partners/partnersApi";
import formatAddress from "app/pages/shipments/utils/formatAddress";
import AttachFileFromButtonContainer from "components/file-uploads/AttachFileFromButtonContainer";
import AttachmentsListContainer from "components/file-uploads/AttachmentsListContainer";
import RecipientTags from "components/RecipientTags";
import Text from "components/Text";
import { CargoGroupContext } from "features/cargo-groups/CargoGroupProvider";
import { selectManyCargoGroups } from "features/cargo-groups/store/cargoGroupsSlice";
import { useCargoGroupsSliceSelector } from "features/cargo-groups/store/cargoGroupsStore";
import useLDFlag from "hooks/useLDFlag";
import { useUserContext } from "hooks/useUserContext";
import capitalize from "lodash/capitalize";
import { DateTime } from "luxon";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import StepCollapse from "../../_components/StepCollapse";
import TemplateOption from "../../_components/TemplateOption";
import { useCreateStepCollapseProps } from "../../_hooks/useStepCollapse";
import { useQuoteRequestSelector } from "../../_store";
import { QuoteStepIndex } from "../../types";
import { FinalSchema } from "../_schemas";

const ReviewStep: VFC<{ onSubmit: (data: FinalSchema) => void }> = ({ onSubmit }) => {
  const { t } = useTranslation(["quoteRequest", "common", "shipper"]);
  const { user } = useUserContext();
  const { stepIndex, quoteRequest: quoteRequest } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);
  const releaseQuoteRequestFiles = useLDFlag("releaseQuoteRequestFiles");
  const { baseStepCollapseProps } = useCreateStepCollapseProps({
    expectedStepIndex: QuoteStepIndex.Review,
    name: t("shipper:reviewAndSend"),
    number: 5,
  });
  const methods = useFormContext<FinalSchema>();

  const { topLevelCargoGroupIds } = useContext(CargoGroupContext);

  const cargoGroups = useCargoGroupsSliceSelector((state) => selectManyCargoGroups(state, topLevelCargoGroupIds));

  const { data: partnerData } = useGetPartnersRequestQuery(
    !!quoteRequest.partners?.length
      ? {
          queryParams: {
            partnerType: "external",
            ids: quoteRequest.partners?.map((partner) => partner.contact_id) ?? [],
          },
        }
      : skipToken
  );
  const { data: teammatesData } = useGetPartnersRequestQuery(
    !!quoteRequest.teammates?.length
      ? {
          queryParams: {
            partnerType: "internal",
            ids: quoteRequest.teammates?.map((teammate) => teammate.contact_id) ?? [],
          },
        }
      : skipToken
  );

  const submit = (data: FinalSchema) => {
    onSubmit(data);
  };

  return (
    <StepCollapse
      {...baseStepCollapseProps}
      footer={
        stepIndex <= QuoteStepIndex.Review ? (
          <div className="flex justify-between mt-2 w-full">
            <div />
            <div className="flex justify-between items-center space-x-3">
              <TemplateOption />
              <Button variant="contained" color="primary" size="large" onClick={methods.handleSubmit(submit)}>
                {t("shipper:sendViaPortex")}
              </Button>
            </div>
          </div>
        ) : null
      }
    >
      <div className="flex flex-col w-full mt-6">
        {/* Email Preview Section */}
        <div className="mb-6">
          <Paper variant="outlined" className="bg-gray-50 p-4">
            <div className="flex flex-col space-y-3 mb-4">
              <div className="flex items-center">
                <span className="font-medium text-sm w-20">{t("quoteRequest:ReviewStep.bcc")}:</span>
                <RecipientTags
                  recipients={
                    partnerData?.data.partners.map((partner) => partner.company_name ?? partner.user.email) ?? []
                  }
                  limit={3}
                  customStyle={{ backgroundColor: "#F3F4F5" }}
                />
              </div>
              <div className="flex items-center">
                <span className="font-medium text-sm w-20">{t("quoteRequest:ReviewStep.cc")}:</span>
                <RecipientTags
                  recipients={teammatesData?.data.partners.map((teammate) => teammate.user.email) ?? []}
                  limit={3}
                  customStyle={{ backgroundColor: "#F3F4F5" }}
                />
              </div>
              <div className="flex items-center mb-4">
                <span className="font-medium text-sm w-20">{t("quoteRequest:ReviewStep.subject")}:</span>
                <TextInput value={quoteRequest.email_metadata?.subject} fullWidth className="bg-white" />
              </div>
            </div>

            {!!releaseQuoteRequestFiles && (
              <div className="flex pb-4 justify-end">
                <AttachFileFromButtonContainer />
              </div>
            )}

            <Paper variant="outlined">
              <div className="flex justify-between p-4 items-center border-b-2">
                <div className="flex flex-col">
                  <Text size="large" weight="bold">
                    {t("common:quoteRequest")}
                  </Text>
                  <Text size="small" typographyProps={{ color: "textSecondary" }}>
                    {quoteRequest.portex_id}
                  </Text>
                </div>

                {quoteRequest.deadline_respond_at ? (
                  <div className="bg-orange-100 text-red-500 py-2 px-2 rounded-[8px]">
                    {t("common:deadlineIn{{time}}", {
                      time: DateTime.fromISO(quoteRequest.deadline_respond_at).toRelative(),
                    })}
                  </div>
                ) : (
                  <div />
                )}
              </div>

              <div className="flex">
                <div className="flex flex-col w-1/2 border-r-2 p-4 space-y-4">
                  <Text size="small" weight="bold">
                    {t("shipper:shipmentDetails")}
                  </Text>

                  <div className="flex flex-col space-y-1">
                    <Text size="small" typographyProps={{ color: "textSecondary" }}>
                      {t("common:mode")}
                    </Text>
                    <Text size="small" weight="bold">
                      {quoteRequest.mode ? t(`common:modeEnum.${quoteRequest.mode}`) : EM_DASH}
                    </Text>
                  </div>

                  <div className="flex flex-col space-y-1">
                    <Text size="small" typographyProps={{ color: "textSecondary" }}>
                      {t("common:shipper")}
                    </Text>
                    <Text size="small" weight="bold">
                      {quoteRequest.shipper_name ?? user?.shipper?.name ?? EM_DASH}
                    </Text>
                  </div>

                  <div className="flex flex-col space-y-1">
                    <Text size="small" typographyProps={{ color: "textSecondary" }}>
                      {t("common:mode")}
                    </Text>
                    <Text size="small" weight="bold">
                      {quoteRequest.mode ? t(`common:modeEnum.${quoteRequest.mode}`) : EM_DASH}
                    </Text>
                  </div>

                  <div className="flex flex-col space-y-1">
                    <Text size="small" typographyProps={{ color: "textSecondary" }}>
                      {t("common:incoterms")}
                    </Text>
                    <Text size="small" weight="bold">
                      {quoteRequest.incoterms ? t(`common:incoterms_map.${quoteRequest.incoterms}`) : EM_DASH}
                    </Text>
                  </div>

                  <div className="flex flex-col space-y-1">
                    <Text size="small" typographyProps={{ color: "textSecondary" }}>
                      {t("shipper:bids.award.moreInfo_bidDeadline")}
                    </Text>
                    <Text size="small" weight="bold">
                      {quoteRequest.deadline_respond_at
                        ? t("common:deadlineIn{{time}}", {
                            time: DateTime.fromISO(quoteRequest.deadline_respond_at).toRelative({ round: true }),
                          })
                        : EM_DASH}
                    </Text>
                  </div>

                  <div className="flex flex-col space-y-2">
                    <Text size="small" weight="bold">
                      {t("common:containers")}
                    </Text>
                    <table>
                      <thead className="bg-grey-50 border-b-2 p-2">
                        <tr>
                          <th className="p-2 text-left">
                            <Text size="small" typographyProps={{ color: "textSecondary" }}>
                              {t("common:containers")}
                            </Text>
                          </th>
                          <th className="p-2 text-left">
                            <Text size="small" typographyProps={{ color: "textSecondary" }}>
                              {t("common:volume")}
                            </Text>
                          </th>
                          <th className="p-2 text-left">
                            <Text size="small" typographyProps={{ color: "textSecondary" }}>
                              {t("common:weight")}
                            </Text>
                          </th>
                          <th className="p-2 text-left">
                            <Text size="small" typographyProps={{ color: "textSecondary" }}>
                              {t("common:commodities")}
                            </Text>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {cargoGroups?.map((cargoGroup) => (
                          <tr>
                            <td className="p-2">
                              <Text size="small" weight="bold">
                                {`${cargoGroup.count} x ${
                                  cargoGroup.equipment_size
                                    ? t(`common:trailerSizes.${cargoGroup.equipment_size}`)
                                    : EM_DASH
                                } ${capitalize(cargoGroup.equipment_type).replaceAll(/_/g, " x ")}`}
                              </Text>
                            </td>
                            <td className="p-2">
                              <Text size="small" weight="bold">
                                {cargoGroup.volume_per_count
                                  ? `${cargoGroup.volume_per_count} ${t("shipper:cbm")}`
                                  : EM_DASH}
                              </Text>
                            </td>
                            <td className="p-2">
                              <Text size="small" weight="bold">
                                {t("common:{{value}}kg", {
                                  value: cargoGroup.weight_per_count ?? 0,
                                })}
                              </Text>
                            </td>
                            <td className="p-2">
                              <Text size="small" weight="bold">
                                {cargoGroup.commodities?.split(",").join(", ")}
                              </Text>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex flex-col space-y-1">
                    <Text size="small" typographyProps={{ color: "textSecondary" }}>
                      {t("common:notes")}
                    </Text>
                    <Text size="small" weight="bold">
                      {quoteRequest.note ? quoteRequest.note : EM_DASH}
                    </Text>
                  </div>

                  <div className="flex flex-col space-y-1">
                    <Text size="small" typographyProps={{ color: "textSecondary" }}>
                      {t("common:attachments")}
                    </Text>
                    <Text size="small" weight="bold">
                      <AttachmentsListContainer showDashOnNoAttachments />
                    </Text>
                  </div>
                </div>

                <div className="flex flex-col w-1/2 p-4 space-y-3">
                  <Text size="small" weight="bold">
                    {t("shipper:locations")}
                  </Text>

                  {quoteRequest.stops?.map((stop, index) => (
                    <div key={index} className="flex flex-col space-y-3">
                      <Text
                        size="small"
                        weight="bold"
                        typographyProps={{ style: { color: index === 0 ? portexColor.blue500 : portexColor.green500 } }}
                      >
                        {index === 0
                          ? t("common:pickup")
                          : index === (quoteRequest.stops?.length ?? 0) - 1
                          ? t("common:delivery")
                          : t("common:stopLabel", { no: index })}
                      </Text>
                      {stop.address && (
                        <>
                          <Text size="small" weight="bold">
                            {formatAddress.fromSnakeCase(stop.address, "short")}
                          </Text>
                          <Text size="x-small" typographyProps={{ color: "textSecondary" }}>
                            {stop.address?.name && (
                              <>
                                {stop.address?.name}
                                <br />
                              </>
                            )}
                            {formatAddress.fromSnakeCase(stop.address, "long")}
                          </Text>
                          {stop.start && (
                            <div className="flex items-center">
                              <CalendarToday className="mr-2 text-gray-500" style={{ fontSize: 16 }} />
                              <Text size="x-small" typographyProps={{ color: "textSecondary" }}>
                                {DateTime.fromISO(stop.start).toFormat("cccc, MMM d")}
                              </Text>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </Paper>
          </Paper>
        </div>
      </div>
    </StepCollapse>
  );
};

export default ReviewStep;
